import { Entity } from "./Entity";
import type { App } from "./App";

/**
 * Clé du périmètre géré (contrat head effectif HA-216/217). `selectors` liste
 * TOUTES les formes rencontrables de la clé — canonique, héritée
 * (`name="og:x"` émis par d'anciens bundles), alias (`twitter:image:src`),
 * doublons `link[rel=canonical]` — et `create` produit la forme CANONIQUE
 * (og:* en `property`, twitter:* en `name`).
 */
interface ManagedKey {
    key: string;
    selectors: string[];
    attr: string;
    create: () => HTMLElement;
    read: (head: Record<string, any>) => string;
}

/**
 * PageHead (Entity) — pipeline meta du `<head>` pour la navigation SPA (ha/443a,
 * réconciliation totale HA-217).
 *
 * `applyHead(app)` pose le `document.title` puis RÉCONCILIE toutes les clés du
 * périmètre géré (liste LITTÉRALE et bornée — revue 13872 : pas de sélecteur
 * global `og:*` qui engloberait `og:locale`) à CHAQUE apply, dès le premier,
 * indépendamment du contenu du payload :
 *
 *  - clé portée par le bloc `head` (App.getHead()) : un UNIQUE nœud subsiste,
 *    à la forme canonique — le nœud canonique existant est adopté (mise à jour
 *    en place, marqué `data-ha-head`), les surnuméraires et les formes
 *    héritées/alias sont RETIRÉS (remplacement, pas retouche : l'attribut
 *    porteur est correct par construction) ;
 *  - clé absente : TOUS les nœuds de la clé sont retirés, y compris les nœuds
 *    SSR jamais adoptés (le résidu « premier payload sans la clé » de la revue
 *    13865 laisse zéro trace) ;
 *  - `robots` : la directive EFFECTIVE de la page courante est résolue chez le
 *    producteur PHP (`HeadContractService::resolveRobots` = politique
 *    d'environnement HA-215 appliquée à la directive de page) et appliquée
 *    TELLE QUELLE. Le client ne déduit rien de l'historique : l'ancienne
 *    « directive initiale du SSR » faisait fuir le noindex de la première page
 *    sur les suivantes (revue 13933). Payload SANS robots = hors contrat →
 *    `noindex,nofollow` (fail-closed : l'erreur ne penche jamais vers
 *    l'indexation, doctrine RuntimeEnv).
 *
 * Les meta hors périmètre (`viewport`, `author`, `csrf-token`, `og:locale`…)
 * ne sont couverts par aucun sélecteur du registre : jamais visités.
 */
export class PageHead extends Entity {

    // Payload hors contrat (robots absent) : fail-closed, jamais l'historique.
    private static readonly ROBOTS_FAIL_CLOSED = "noindex,nofollow";

    public applyHead(app?: App): void {
        if (!app) { return; }
        const head = this.readHead(app);

        this.applyTitle(head, app);
        this.applyPageCss(app);

        for (const managed of this.managedKeys()) {
            this.reconcile(managed, managed.read(head));
        }
    }

    /**
     * CSS de la page pour la navigation SPA (todo/ha/spa-page-css-injection.md).
     * Le CSS compilé par page (payload `pageCss`, scopé `[data-ha-page="<pageId>"]`)
     * est intégré dans un UNIQUE `<style id="ha-spa-page-css">` swappé à chaque
     * navigation (pas d'accumulation), et le marqueur de scope `data-ha-page` du
     * `<body>` bascule vers la page cible — sans lui, le CSS scopé ne matche rien
     * (le converter namespace chaque page sous cet attribut, cf. HTMLPage SSR).
     * Payload sans `pageCss` (full-load hydraté, page legacy) : seul le marqueur est
     * rétabli (HA-275) — le style SSR inline du head reste seul en jeu.
     */
    private applyPageCss(app: App): void {
        // HA-275 : le marqueur suit TOUJOURS l'app affichée, pageCss ou non. Au retour
        // d'une modale (pageCss scopé sur SON id) vers une page chargée en entier (CSS
        // dans le <style> SSR, pas de pageCss), le body gardait l'id de la modale et le
        // CSS de la page cessait de s'appliquer.
        const pageId = app.getId ? String(app.getId() || "") : "";
        if (pageId) {
            document.body.setAttribute("data-ha-page", pageId);
        }

        const css = app.getPageCss ? app.getPageCss() : "";
        if (!css) { return; }

        let el = document.getElementById("ha-spa-page-css") as HTMLStyleElement | null;
        if (!el) {
            el = document.createElement("style");
            el.id = "ha-spa-page-css";
            document.head.appendChild(el);
        }
        el.textContent = css;
    }

    /**
     * Normalise le bloc `head` du payload en objet plat. `toMap` étant peu
     * profond, `getHead()` peut renvoyer un objet natif, une Map, ou rien.
     */
    private readHead(app: App): Record<string, any> {
        return this.asPlain(app.getHead ? app.getHead() : undefined);
    }

    private asPlain(value: any): Record<string, any> {
        if (!value) { return {}; }
        if (value instanceof Map) {
            const out: Record<string, any> = {};
            value.forEach((v, k) => { out[String(k)] = v; });
            return out;
        }
        return value as Record<string, any>;
    }

    private applyTitle(head: Record<string, any>, app: App): void {
        let title = head.title;
        if (!title && app.getName) { title = app.getName(); }
        if (typeof title === "string" && title.length > 0) {
            document.title = title;
        }
    }

    /**
     * Registre LITTÉRAL du périmètre géré. `og:locale`, `viewport`,
     * `csrf-token`, `author`… n'y figurent pas : hors périmètre par
     * construction (aucune boucle générique sur les clés du payload).
     */
    private managedKeys(): ManagedKey[] {
        const og = ["title", "description", "image", "type", "url", "site_name"];
        const twitter = ["card", "title", "description", "image", "url"];
        const keys: ManagedKey[] = [
            {
                key: "meta:name:description",
                selectors: ['meta[name="description"]'],
                attr: "content",
                create: () => this.metaName("description"),
                read: (head) => this.str(head.description),
            },
            {
                key: "meta:name:robots",
                selectors: ['meta[name="robots"]'],
                attr: "content",
                // directive EFFECTIVE résolue par le producteur (HA-215) ;
                // absente = hors contrat → fail-closed, jamais un retrait.
                create: () => this.metaName("robots"),
                read: (head) => this.str(head.robots) || PageHead.ROBOTS_FAIL_CLOSED,
            },
            {
                key: "link:canonical",
                selectors: ['link[rel="canonical"]'],
                attr: "href",
                create: () => {
                    const el = document.createElement("link");
                    el.setAttribute("rel", "canonical");
                    return el;
                },
                read: (head) => this.str(head.canonical),
            },
        ];
        for (const name of og) {
            keys.push({
                key: "meta:property:og:" + name,
                // forme canonique property + forme héritée name (anciens bundles)
                selectors: [`meta[property="og:${name}"]`, `meta[name="og:${name}"]`],
                attr: "content",
                create: () => this.metaProperty("og:" + name),
                read: (head) => this.str(this.asPlain(head.og)[name]),
            });
        }
        for (const name of twitter) {
            const selectors = [`meta[name="twitter:${name}"]`, `meta[property="twitter:${name}"]`];
            if (name === "image") {
                // alias historique émis par Head.php avant HA-216
                selectors.push('meta[name="twitter:image:src"]', 'meta[property="twitter:image:src"]');
            }
            keys.push({
                key: "meta:name:twitter:" + name,
                selectors,
                attr: "content",
                create: () => this.metaName("twitter:" + name),
                read: (head) => this.str(this.asPlain(head.twitter)[name]),
            });
        }

        return keys;
    }

    /**
     * Réconciliation d'une clé : recense TOUS les nœuds de TOUTES ses formes.
     * Valeur présente → adopte le premier nœud à la forme CANONIQUE (mise à
     * jour en place) ou en crée un, et retire les surnuméraires/hérités.
     * Valeur absente → retire tout, y compris les nœuds SSR jamais adoptés.
     */
    private reconcile(managed: ManagedKey, value: string): void {
        const nodes: HTMLElement[] = [];
        for (const selector of managed.selectors) {
            document.head.querySelectorAll(selector).forEach((el) => {
                const html = el as HTMLElement;
                if (!nodes.includes(html)) { nodes.push(html); }
            });
        }

        if (!value) {
            for (const el of nodes) {
                if (el.parentNode) { el.parentNode.removeChild(el); }
            }
            return;
        }

        // le nœud adopté doit être à la forme canonique (premier sélecteur)
        const canonical = nodes.find((el) => el.matches(managed.selectors[0])) || null;
        for (const el of nodes) {
            if (el !== canonical && el.parentNode) { el.parentNode.removeChild(el); }
        }
        const target = canonical || managed.create();
        if (!target.parentNode) { document.head.appendChild(target); }
        target.setAttribute("data-ha-head", managed.key);
        target.setAttribute(managed.attr, value);
    }

    private str(value: any): string {
        return typeof value === "string" ? value.trim() : "";
    }

    private metaName(name: string): HTMLElement {
        const el = document.createElement("meta");
        el.setAttribute("name", name);
        return el;
    }

    private metaProperty(property: string): HTMLElement {
        const el = document.createElement("meta");
        el.setAttribute("property", property);
        return el;
    }
}
